// ABOUTME: The mobile reel's bottom composition (SPEC-26 Phase 4b) — CC bar with the keyboard and
// ABOUTME: mic stacked in a column to its right. Figma `Frame 168` + `Group 158`, all 7 mobile frames.

import React, { useEffect, useRef } from 'react';
import { Camera, Square } from 'lucide-react';
import { MOBILE_UNIT, mu, muMin, safeBottom, safeRight, FOOTER, FOOTER_STOP_SLOT } from './mobileGeometry';
import CaptionOverlay from '../Projectionist/overlays/CaptionOverlay';
import VoiceDock, { dockSlotWidth } from '../VoiceFirst/VoiceDock/VoiceDock'; // SPEC-82
import { buildDockControls } from '../VoiceFirst/VoiceDock/RevealedControls'; // SPEC-82 D-10

// The same 120x117.33 boxes as desktop (45:1197 / 45:1211); mobile just stacks them instead of
// flanking. The glyph artwork is smaller than its box, so the box is the hit target.
const KEYBOARD_GLYPH = { src: '/reel-chrome/icon-keyboard.svg', w: 72, h: 49.592 };
const MIC_GLYPH = { src: '/reel-chrome/icon-mic.svg', w: 46, h: 73.459 };
const MIC_GLYPH_OFF = { src: '/reel-chrome/icon-mic-off.svg', w: 72.119, h: 75.346 };
const CAPTION_LINE_HEIGHT = 1.15;
// Same 2-line clamp as the desktop field (see ReelFooter) — the stacked bar's height is fixed too.
const INPUT_VISIBLE_LINES = 2;
const INPUT_MAX_HEIGHT = FOOTER.captionFont * CAPTION_LINE_HEIGHT * INPUT_VISIBLE_LINES;

// iOS Safari zooms the whole page in when a text field smaller than 16px takes focus, and there is
// no way back out of that zoom without a pinch — on a fixed full-screen surface that strands the
// guest mid-conversation. The authored caption size resolves to ~12px on a handset, so this floor
// is always the one in play there. Exported because jsdom drops `max()`/`calc()` from the CSSOM, so
// a test cannot read it back off the rendered field; asserting the constant is what pins it.
const IOS_NO_ZOOM_FONT_PX = 16;
export const MOBILE_FIELD_FONT_SIZE = muMin(FOOTER.captionFont, IOS_NO_ZOOM_FONT_PX);

// `disabled` is separate from `dimmed` on purpose — see the same note on the desktop GlyphButton.
// `reel-touch-target` stays on the class list unconditionally: MobileFooter.test.js enumerates every
// button and fails if one is missing the 44px hit area, disabled or not.
function GlyphButton({ glyph, label, title, onClick, dimmed = false, disabled = false, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title || label}
      onClick={onClick}
      disabled={disabled}
      className={`reel-touch-target relative flex items-center justify-center shrink-0 bg-reel-chrome-panel backdrop-blur-reel-chrome rounded-reel-iconButton hover:bg-white/10 transition-colors ${dimmed ? 'opacity-40' : 'opacity-100'}`}
      style={{ width: mu(FOOTER.iconW), height: mu(FOOTER.iconH) }}
    >
      {glyph ? (
        <img src={glyph.src} alt="" aria-hidden="true" style={{ width: mu(glyph.w), height: mu(glyph.h) }} />
      ) : children}
    </button>
  );
}

/**
 * The mobile footer.
 *
 * The bar's authored width (1009) is not hardcoded — it is what remains once the two 37 side insets,
 * the 13 gutter and the 120-wide icon column are taken out of the 1216 frame, which is exactly how
 * the design arrives at it. Deriving it means the bar cannot drift out of alignment with the column
 * on a viewport the design never anticipated.
 *
 * Two documented departures, both carried over from the desktop composition's own (SPEC-26 OQ-R5):
 *  - the tenant-flagged camera has no slot in any frame and rides at the top of the column;
 *  - there is no CC-toggle control, because the design has none — `ccEnabled` is App state.
 */
function MobileFooter({
  ccEnabled,
  isKeyboardOpen, onOpenKeyboard, onCloseKeyboard,
  textInput, onTextInputChange, onSubmitText,
  isMicOn, onToggleMic,
  visualizer = null,
  agentState,
  showCamera = false, onCamera,
  showStop = false, canStop = false, onStopAgent,
  voiceFirst = null, // SPEC-82: null (every flag-off tenant) => the markup below is exactly today's
}) {
  const textareaRef = useRef(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [textInput]);

  const input = isKeyboardOpen ? (
    <form onSubmit={onSubmitText} className="flex items-center gap-2 w-full pointer-events-auto">
      <textarea
        ref={textareaRef}
        rows={1}
        value={textInput}
        onChange={(e) => onTextInputChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends, same as the old single-line field; Shift+Enter still breaks the line.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmitText(e);
          }
        }}
        placeholder="Type a message..."
        // Distinct from the keyboard button's "Type a message" — two controls sharing one
        // accessible name is ambiguous for screen readers, and unresolvable for tests.
        aria-label="Message"
        autoFocus
        disabled={agentState === 'thinking'}
        className="flex-1 min-w-0 resize-none overflow-y-hidden bg-transparent border-none focus:outline-none text-reel-chrome-textPrimary placeholder:text-white/40 font-reel-medium"
        // 16px floor rather than the chrome's 11px one — see MOBILE_FIELD_FONT_SIZE above.
        style={{ fontSize: MOBILE_FIELD_FONT_SIZE, lineHeight: CAPTION_LINE_HEIGHT, maxHeight: mu(INPUT_MAX_HEIGHT) }}
      />
    </form>
  ) : null;

  return (
    <div
      className="absolute flex items-end pointer-events-none"
      style={{
        '--u': MOBILE_UNIT,
        bottom: safeBottom(FOOTER.barBottom),
        left: mu(FOOTER.barLeft),
        right: safeRight(FOOTER.barLeft),
        gap: mu(FOOTER.iconGutter),
      }}
    >
      <div
        className={voiceFirst?.dock ? 'flex-1 min-w-0 relative' : 'flex-1 min-w-0'}
        style={{ height: mu(FOOTER.barH), pointerEvents: 'auto' }}
      >
        <CaptionOverlay.Bar
          enabled={ccEnabled}
          visualizer={voiceFirst?.dock ? null : visualizer}
          input={input}
          layout="stacked"
          unit={MOBILE_UNIT}
          {...(voiceFirst ? { bare: voiceFirst.bare, clusterW: voiceFirst.dock ? dockSlotWidth('mobile', voiceFirst.ring) : undefined } : {})}
        />
        {/* SPEC-82: the dock lives beside the clipping bar (inside this positioned box), at the cluster slot. */}
        {voiceFirst?.dock && (
          <VoiceDock
            composition="mobile"
            core={visualizer}
            ring={voiceFirst.ring}
            talk={voiceFirst.talk}
            reveal={voiceFirst.reveal}
            controls={buildDockControls({
              isKeyboardOpen, onOpenKeyboard, onCloseKeyboard, isMicOn, onToggleMic, showStop, canStop, onStopAgent, showCamera, onCamera,
            })}
            reducedMotion={voiceFirst.reducedMotion}
            connecting={voiceFirst.connecting || null} // 2026-10-08: 'Connecting to …' while the agent starts
          />
        )}
      </div>

      {/* Manual interrupt (SPEC-73) — see the fuller note on ReelFooter. Shown in voice mode as well
          as keyboard mode, for tenants that set `manual_interrupt`. It gets its OWN slot beside the
          icon column, level with the keyboard box, rather than a third box in the column: the
          column is exactly bar-height and clears the tag row, and a third box would rise over the
          right-most point tag (FOOTER_STOP_SLOT). The lift is a margin, not padding, so the empty
          space beside the mic is not a dead hit area. Not rendered at all when `showStop` is false,
          so a tenant without the flag keeps byte-identical markup. */}
      {showStop && !voiceFirst?.hideFlanks && (
        <div
          className="shrink-0"
          style={{ width: mu(FOOTER.iconW), marginBottom: mu(FOOTER_STOP_SLOT.lift), pointerEvents: 'auto' }}
        >
          <GlyphButton
            label="Stop the agent"
            dimmed={!canStop}
            disabled={!canStop}
            onClick={onStopAgent}
          >
            <Square size={16} fill="currentColor" className="text-reel-chrome-textPrimary" />
          </GlyphButton>
        </div>
      )}

      {/* The column's own height is the two boxes plus the gutter — 117.33 + 13 + 117.33 = 247.67,
          which is the bar's height and the authored 130.33 pitch, both at once. It is not pinned to
          the bar's height, so the optional camera grows it upward instead of squeezing the two
          controls the design does author. */}
      {!voiceFirst?.hideFlanks && (
      <div
        className="shrink-0 flex flex-col justify-end"
        style={{ width: mu(FOOTER.iconW), gap: mu(FOOTER.iconGutter), pointerEvents: 'auto' }}
      >
        {showCamera && (
          <GlyphButton label="Share a photo of your own piece or watch" onClick={onCamera}>
            <Camera size={16} className="text-reel-chrome-textPrimary" />
          </GlyphButton>
        )}
        <GlyphButton
          label={isKeyboardOpen ? 'Hide text input' : 'Type a message'}
          title={isKeyboardOpen ? 'Hide text input' : 'Type a message'}
          glyph={KEYBOARD_GLYPH}
          dimmed={!isKeyboardOpen}
          onClick={isKeyboardOpen ? onCloseKeyboard : onOpenKeyboard}
        />
        <GlyphButton
          label={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
          glyph={isMicOn ? MIC_GLYPH : MIC_GLYPH_OFF}
          dimmed={!isMicOn}
          onClick={onToggleMic}
        />
      </div>
      )}
    </div>
  );
}

export default MobileFooter;
